import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Layout, LayoutColumn } from '@src/suite/common/data-models/service/layout';

import { StColumnsSelectorComponent } from '../../components/st-columns-selector/st-columns-selector.component';
import { PSTFieldNames } from '../../data-models/fields/pst-field-names';
import { selectFieldDefsDict, selectStaffUsers } from '../../store/global-tables/global-tables.selectors';
import { selectI18N } from '../../store/i18n/selectors/i18n.selectors';
import { isRundownProperty } from '../../utils/global-tables.utils';
import { convertFieldDefToLayoutColumn } from '../../utils/record-utils';

@Component({
    selector: 'st-rundown-choose-columns',
    imports: [MatButton, StColumnsSelectorComponent],
    templateUrl: './st-rundown-choose-columns.component.html',
    styleUrl: './st-rundown-choose-columns.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rundown-choose-columns',
    },
})
export class StRundownChooseColumnsComponent {
    data = inject<{
        layout: Layout;
        pageNumEditable?: boolean;
    }>(MAT_DIALOG_DATA);

    store = inject(Store);
    dialogRef = inject(MatDialogRef<StRundownChooseColumnsComponent>);

    public languageSettings = this.store.selectSignal(selectI18N)();
    private fieldDefs = this.store.selectSignal(selectFieldDefsDict)();
    private staffUsers = this.store.selectSignal(selectStaffUsers)();

    readonly disableUpdate = signal<boolean>(true);
    readonly pageNumEditable = signal<boolean>(false);

    public readonly columnsFieldDefs = computed(() => {
        return Object.values(this.fieldDefs)?.filter(fieldDef => isRundownProperty(fieldDef) && fieldDef?.ID !== PSTFieldNames.RowStatus && fieldDef?.ID !== PSTFieldNames.SegStatus);
    });

    readonly hiddenColumns = [PSTFieldNames.RowStatus, PSTFieldNames.SegStatus];
    readonly nonRemovalColumns = [PSTFieldNames.PageNum, PSTFieldNames.RowStatus, PSTFieldNames.Slug, PSTFieldNames.SegStatus, PSTFieldNames.Segment];

    readonly fixedColumns = signal<StorytellingColumnDefinition[]>([]);
    readonly selectedColumns = signal<StorytellingColumnDefinition[]>([]);
    readonly value = signal<StorytellingColumnDefinition[]>([]);

    readonly layoutColumns = signal<LayoutColumn[] | null>(null);

    constructor() {
        this.pageNumEditable.set(this.data?.pageNumEditable);
        this.layoutColumns.set(this.data?.layout?.columns);
        if (this.layoutColumns()?.length) {
            const nonRemovalColumnsSet = new Set(this.nonRemovalColumns);
            const selectedColumns =
                this.layoutColumns()
                    ?.filter(col => !nonRemovalColumnsSet.has(col.name as PSTFieldNames))
                    ?.map(col => {
                        return {
                            id: col.name,
                            title: col.name || this.fieldDefs[col.name]?.DefaultCaption,
                            order: col.sortOrder,
                        };
                    }) || [];
            const fixedColumns =
                this.layoutColumns()
                    ?.filter(col => nonRemovalColumnsSet.has(col.name as PSTFieldNames))
                    ?.map(col => {
                        return {
                            id: col.name,
                            title: col.name || this.fieldDefs[col.name]?.DefaultCaption,
                            order: col.sortOrder,
                        };
                    }) || [];
            this.selectedColumns.set(selectedColumns);
            this.fixedColumns.set(fixedColumns);
        }
    }

    onColumnSelectionChanged(columns: StorytellingColumnDefinition[]) {
        this.value.set(columns);
        this.disableUpdate.set(!(this.value()?.length > 0));
    }

    close() {
        this.dialogRef.close();
    }

    update() {
        const columns = this.value().map((col, index) => {
            const layoutColumn = this.layoutColumns().find(lc => lc.name === col.id);
            return { ...(layoutColumn || convertFieldDefToLayoutColumn(this.fieldDefs[col.id], this.staffUsers)), sortOrder: index };
        });
        this.dialogRef.close({ columns });
    }
}
